import {
  BookOpen,
  CheckCircle2,
  ChevronDown,
  FileText,
  Library,
  LoaderCircle,
  MessageSquareText,
  Settings2,
} from "lucide-react";
import { CitationCard, Justiniano } from "@tribunalea/justiniano";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "@tribunalea/ui";
import { useApi } from "../App.jsx";
import { LibraryPdfPreview } from "../components/LibraryPdfPreview.jsx";

const pendingStatuses = new Set([
  "uploading",
  "queued",
  "extracting",
  "indexing",
]);

const statusLabels = {
  uploading: "Guardando",
  queued: "En cola",
  extracting: "Extrayendo texto",
  indexing: "Creando índice",
  ready: "Lista",
  failed: "Con error",
};

const PANEL_WIDTH_STORAGE_KEY = "instrumenta:library-ai:panel-widths";
const PANEL_LIMITS = {
  libraryMin: 220,
  libraryMax: 420,
  chatMin: 390,
  previewMin: 300,
  previewMax: 720,
  handles: 20,
};

function clamp(value, minimum, maximum) {
  return Math.max(minimum, Math.min(value, maximum));
}

function storedPanelWidths() {
  try {
    const stored = JSON.parse(
      window.localStorage.getItem(PANEL_WIDTH_STORAGE_KEY) || "{}",
    );
    return {
      library: Number(stored.library) || 270,
      preview: Number(stored.preview) || 420,
    };
  } catch {
    return { library: 270, preview: 420 };
  }
}

function initialSourceFromUrl(notebookId) {
  const parameters = new URLSearchParams(window.location.search);
  const sourceId = parameters.get("source");
  if (!notebookId || !sourceId) return null;
  return {
    notebookId,
    sourceId,
    fileName: "Fuente del cuaderno",
    page: Number(parameters.get("page")) || null,
  };
}

export function LibraryAiPage({ getAccessToken, initialNotebookId = null }) {
  const api = useApi();
  const { navigate } = useRouter();
  const workspaceRef = useRef(null);
  const [notebooks, setNotebooks] = useState([]);
  const [activeNotebookId, setActiveNotebookId] = useState(
    initialNotebookId
      || new URLSearchParams(window.location.search).get("notebook")
      || window.localStorage.getItem("instrumenta:library-ai:notebook")
      || null,
  );
  const [notebook, setNotebook] = useState(null);
  const [loadingNotebooks, setLoadingNotebooks] = useState(true);
  const [loadingNotebook, setLoadingNotebook] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(() =>
    initialSourceFromUrl(initialNotebookId));
  const [previewPageState, setPreviewPageState] = useState(null);
  const [mobilePanel, setMobilePanel] = useState("chat");
  const [workspaceWidth, setWorkspaceWidth] = useState(0);
  const [panelWidths, setPanelWidths] = useState(storedPanelWidths);

  const loadNotebooks = useCallback(async () => {
    try {
      const items = await api.listNotebooks();
      setNotebooks(items);
      setActiveNotebookId((current) => {
        if (current && items.some(({ id }) => id === current)) return current;
        return items[0]?.id || null;
      });
      setError("");
    } catch (reason) {
      setError(reason.message);
    } finally {
      setLoadingNotebooks(false);
    }
  }, [api]);

  const loadNotebook = useCallback(async ({ quiet = false } = {}) => {
    if (!activeNotebookId) {
      setNotebook(null);
      return;
    }
    if (!quiet) setLoadingNotebook(true);
    try {
      const item = await api.getNotebook(activeNotebookId);
      setNotebook(item);
      setNotebooks((current) => current.map((candidate) =>
        candidate.id === item.id
          ? {
              ...candidate,
              name: item.name,
              description: item.description,
              sourceCount: item.sourceCount,
              readySourceCount: item.readySourceCount,
              pageCount: item.pageCount,
            }
          : candidate));
      setPreview((current) => {
        if (!current || current.notebookId !== item.id) return current;
        const source = item.sources.find(({ id }) => id === current.sourceId);
        return source
          ? { ...current, fileName: source.name || source.fileName }
          : null;
      });
      setError("");
    } catch (reason) {
      setError(reason.message);
    } finally {
      if (!quiet) setLoadingNotebook(false);
    }
  }, [activeNotebookId, api]);

  useEffect(() => {
    void loadNotebooks();
  }, [loadNotebooks]);

  useEffect(() => {
    const element = workspaceRef.current;
    if (!element || !window.ResizeObserver) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setWorkspaceWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    window.localStorage.setItem(
      PANEL_WIDTH_STORAGE_KEY,
      JSON.stringify(panelWidths),
    );
  }, [panelWidths]);

  useEffect(() => {
    window.localStorage.setItem(
      "instrumenta:library-ai:notebook",
      activeNotebookId || "",
    );
    void loadNotebook();
  }, [activeNotebookId, loadNotebook]);

  useEffect(() => {
    if (!notebook?.sources.some(({ status }) => pendingStatuses.has(status))) {
      return undefined;
    }
    const timer = window.setInterval(
      () => void loadNotebook({ quiet: true }),
      2_500,
    );
    return () => window.clearInterval(timer);
  }, [loadNotebook, notebook]);

  const effectivePanelWidths = useMemo(() => {
    if (!workspaceWidth) return panelWidths;
    const usable = workspaceWidth - (preview ? PANEL_LIMITS.handles : 10);
    if (!preview) {
      return {
        library: clamp(
          panelWidths.library,
          PANEL_LIMITS.libraryMin,
          Math.max(
            PANEL_LIMITS.libraryMin,
            Math.min(
              PANEL_LIMITS.libraryMax,
              usable - PANEL_LIMITS.chatMin,
            ),
          ),
        ),
        preview: panelWidths.preview,
      };
    }
    const libraryMaximum = Math.max(
      PANEL_LIMITS.libraryMin,
      Math.min(
        PANEL_LIMITS.libraryMax,
        usable - PANEL_LIMITS.chatMin - PANEL_LIMITS.previewMin,
      ),
    );
    const library = clamp(
      panelWidths.library,
      PANEL_LIMITS.libraryMin,
      libraryMaximum,
    );
    const previewMaximum = Math.max(
      PANEL_LIMITS.previewMin,
      Math.min(
        PANEL_LIMITS.previewMax,
        usable - PANEL_LIMITS.chatMin - library,
      ),
    );
    return {
      library,
      preview: clamp(
        panelWidths.preview,
        PANEL_LIMITS.previewMin,
        previewMaximum,
      ),
    };
  }, [panelWidths, preview, workspaceWidth]);

  function setPanelWidth(panel, value) {
    const usable = workspaceWidth - (preview ? PANEL_LIMITS.handles : 10);
    setPanelWidths((current) => {
      if (panel === "library") {
        const maximum = Math.min(
          PANEL_LIMITS.libraryMax,
          usable
            - PANEL_LIMITS.chatMin
            - (preview ? effectivePanelWidths.preview : 0),
        );
        return {
          ...current,
          library: clamp(
            value,
            PANEL_LIMITS.libraryMin,
            Math.max(PANEL_LIMITS.libraryMin, maximum),
          ),
        };
      }
      const maximum = Math.min(
        PANEL_LIMITS.previewMax,
        usable - PANEL_LIMITS.chatMin - effectivePanelWidths.library,
      );
      return {
        ...current,
        preview: clamp(
          value,
          PANEL_LIMITS.previewMin,
          Math.max(PANEL_LIMITS.previewMin, maximum),
        ),
      };
    });
  }

  function startResize(panel, event) {
    if (window.matchMedia("(max-width: 1200px)").matches) return;
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = effectivePanelWidths[panel];
    document.body.classList.add("is-resizing-library-ai");

    const onPointerMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      setPanelWidth(
        panel,
        panel === "library" ? startWidth + delta : startWidth - delta,
      );
    };
    const stop = () => {
      document.body.classList.remove("is-resizing-library-ai");
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
  }

  function resizeWithKeyboard(panel, event) {
    if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
    event.preventDefault();
    const direction = event.key === "ArrowRight" ? 1 : -1;
    const delta = panel === "library" ? direction * 16 : direction * -16;
    setPanelWidth(panel, effectivePanelWidths[panel] + delta);
  }

  function selectNotebook(id) {
    const nextId = activeNotebookId === id ? null : id;
    setActiveNotebookId(nextId);
    setMobilePanel("library");
    window.history.replaceState(
      {},
      "",
      nextId ? `/biblioteca/${nextId}` : "/biblioteca",
    );
  }

  function openSource(source, page = null) {
    if (source.status !== "ready") return;
    setPreviewPageState(null);
    setPreview({
      notebookId: notebook.id,
      sourceId: source.id,
      fileName: source.name || source.fileName,
      page,
    });
    setMobilePanel("preview");
  }

  function openCitation(citation) {
    const metadata = citation.metadata || {};
    if (!metadata.notebookId || !metadata.librarySourceId) return;
    setActiveNotebookId(metadata.notebookId);
    setPreviewPageState(null);
    setPreview({
      notebookId: metadata.notebookId,
      sourceId: metadata.librarySourceId,
      fileName: metadata.name || citation.label || metadata.fileName || "Fuente del cuaderno",
      page: metadata.pageStart || null,
    });
    setMobilePanel("preview");
  }

  const renderers = useMemo(() => ({
    renderCitation: ({ citation, index }) => {
      if (
        citation.category !== "library"
        && citation.type !== "library"
        && !citation.metadata?.librarySourceId
      ) {
        return null;
      }
      const internalCitation = {
        ...citation,
        source: {
          ...(citation.source || citation.metadata?.source || {}),
          url: null,
        },
      };
      return (
        <div className="library-ai-citation">
          <CitationCard citation={internalCitation} index={index} />
          <button type="button" onClick={() => openCitation(citation)}>
            Ver fuente original
          </button>
        </div>
      );
    },
  }), []);

  return (
    <div className={`library-ai-page mobile-panel-${mobilePanel}`}>
      <header className="library-ai-topbar">
        <div>
          <span className="library-ai-mark"><Library size={18} /></span>
          <span>
            <strong>Biblioteca IA</strong>
            <small>Cuadernos, fuentes y Justiniano en un mismo espacio</small>
          </span>
        </div>
        <button
          className="library-ai-manage-button"
          type="button"
          onClick={() => navigate("/biblioteca/administrar")}
        >
          <Settings2 size={15} />
          Administrar biblioteca
        </button>
        <nav aria-label="Secciones de Biblioteca IA">
          <button
            className={mobilePanel === "library" ? "is-active" : ""}
            type="button"
            onClick={() => setMobilePanel("library")}
          >
            <BookOpen size={15} /> <span>Cuadernos</span>
          </button>
          <button
            className={mobilePanel === "chat" ? "is-active" : ""}
            type="button"
            onClick={() => setMobilePanel("chat")}
          >
            <MessageSquareText size={15} /> <span>Justiniano</span>
          </button>
          <button
            className={mobilePanel === "preview" ? "is-active" : ""}
            type="button"
            disabled={!preview}
            onClick={() => preview && setMobilePanel("preview")}
          >
            <FileText size={15} /> <span>Documento</span>
          </button>
        </nav>
      </header>

      <div
        className={`library-ai-workspace ${preview ? "has-preview" : ""}`}
        ref={workspaceRef}
        style={{
          "--library-ai-library-width": `${effectivePanelWidths.library}px`,
          "--library-ai-preview-width": `${effectivePanelWidths.preview}px`,
        }}
      >
        <aside className="library-ai-library">
          <header>
            <div>
              <span className="eyebrow">TUS CUADERNOS</span>
              <strong>{notebooks.length} cuaderno{notebooks.length === 1 ? "" : "s"}</strong>
            </div>
            <button
              className="icon-button"
              type="button"
              title="Administrar biblioteca"
              onClick={() => navigate("/biblioteca/administrar")}
            >
              <Settings2 size={17} />
            </button>
          </header>

          <div className="library-ai-notebooks">
            {loadingNotebooks ? (
              <div className="library-ai-inline-status">
                <LoaderCircle className="is-spinning" size={17} />
                Cargando…
              </div>
            ) : notebooks.length ? notebooks.map((item) => {
              const expanded = activeNotebookId === item.id;
              return (
                <section
                  className={`library-ai-notebook-accordion ${
                    expanded ? "is-expanded" : ""
                  }`}
                  key={item.id}
                >
                  <button
                    className="library-ai-notebook-trigger"
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => selectNotebook(item.id)}
                  >
                    <span><BookOpen size={17} /></span>
                    <strong>{item.name}</strong>
                    <ChevronDown size={15} />
                  </button>

                  {expanded && (
                    <div className="library-ai-accordion-content">
                      {loadingNotebook ? (
                        <div className="library-ai-inline-status">
                          <LoaderCircle className="is-spinning" size={16} />
                          Abriendo cuaderno…
                        </div>
                      ) : notebook?.id === item.id ? (
                        notebook.sources.length ? (
                          <div className="library-ai-source-list">
                            {notebook.sources.map((source) => (
                              <article
                                className={[
                                  `is-${source.status}`,
                                  preview?.sourceId === source.id
                                    ? "is-active"
                                    : "",
                                ].join(" ")}
                                key={source.id}
                                onClick={() => openSource(source)}
                              >
                                <span className="library-ai-source-icon">
                                  {source.status === "ready"
                                    ? <CheckCircle2 size={16} />
                                    : pendingStatuses.has(source.status)
                                      ? (
                                        <LoaderCircle
                                          className="is-spinning"
                                          size={16}
                                        />
                                      )
                                      : <FileText size={16} />}
                                </span>
                                <span>
                                  <strong title={source.fileName}>
                                    {source.name || source.fileName}
                                  </strong>
                                  <small>
                                    {statusLabels[source.status]}
                                    {source.pageCount
                                      ? ` · ${source.pageCount} pág.`
                                      : ""}
                                  </small>
                                </span>
                              </article>
                            ))}
                          </div>
                        ) : (
                          <div className="library-ai-no-sources">
                            <FileText size={20} />
                            <span>Este cuaderno no tiene PDFs</span>
                          </div>
                        )
                      ) : null}
                    </div>
                  )}
                </section>
              );
            }) : (
              <button
                className="library-ai-first-notebook"
                type="button"
                onClick={() => navigate("/biblioteca/administrar")}
              >
                <Settings2 size={17} />
                Crear el primer cuaderno
              </button>
            )}
          </div>

          {error && <p className="library-ai-error">{error}</p>}
        </aside>

        <div
          className="library-ai-resizer is-library"
          role="separator"
          aria-label="Cambiar ancho de cuadernos"
          aria-orientation="vertical"
          aria-valuemin={PANEL_LIMITS.libraryMin}
          aria-valuemax={PANEL_LIMITS.libraryMax}
          aria-valuenow={Math.round(effectivePanelWidths.library)}
          tabIndex={0}
          onDoubleClick={() => setPanelWidth("library", 270)}
          onKeyDown={(event) => resizeWithKeyboard("library", event)}
          onPointerDown={(event) => startResize("library", event)}
        />

        <main className="library-ai-chat">
          <Justiniano
            allowAttachments
            variant="page"
            appId="instrumenta"
            appName="Instrumenta"
            apiUrl={import.meta.env.VITE_API_URL || "http://localhost:4000"}
            getAccessToken={getAccessToken}
            notebookScope="all"
            renderers={renderers}
            welcome={
              notebooks.length
                ? `Consulto automáticamente las fuentes listas de todos tus cuadernos (${notebooks.length}). A la izquierda podés abrir cualquiera para revisar sus documentos.`
                : "Todavía no tenés cuadernos. Igualmente podés hacerme una consulta jurídica."
            }
            suggestions={[
              "Resumí los puntos principales de mis cuadernos",
              "¿Qué temas aparecen en mi biblioteca?",
              "¿Qué fuentes jurídicas podés consultar?",
            ]}
            appContext={{
              route: "/biblioteca",
              interfaceHelp: true,
              activeEntity: preview ? {
                type: "library_source",
                id: preview.sourceId,
                label: preview.fileName,
              } : null,
              pageState: {
                documentOpen: Boolean(preview),
                documentType: "pdf",
                openNotebookId: notebook?.id || null,
                sourcePreviewOpen: Boolean(preview),
                sourceId: preview?.sourceId || null,
                fileName: preview?.fileName || null,
                ...(previewPageState?.sourceId === preview?.sourceId
                  ? previewPageState
                  : {}),
              },
            }}
          />
        </main>

        <div
          className="library-ai-resizer is-preview"
          role="separator"
          aria-label="Cambiar ancho del visor PDF"
          aria-orientation="vertical"
          aria-valuemin={PANEL_LIMITS.previewMin}
          aria-valuemax={PANEL_LIMITS.previewMax}
          aria-valuenow={Math.round(effectivePanelWidths.preview)}
          tabIndex={0}
          onDoubleClick={() => setPanelWidth("preview", 420)}
          onKeyDown={(event) => resizeWithKeyboard("preview", event)}
          onPointerDown={(event) => startResize("preview", event)}
        />

        <LibraryPdfPreview
          document={preview}
          onPageChange={setPreviewPageState}
          onClose={() => {
            setPreview(null);
            setPreviewPageState(null);
            setMobilePanel("chat");
          }}
        />
      </div>
    </div>
  );
}
